<script setup lang="ts">
import '@/assets/scss/dashboard.scss'
import { PerfectScrollbar } from 'vue3-perfect-scrollbar'
import { computed, onMounted, ref, watch } from 'vue'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Icon } from '@iconify/vue'
import { useAuthStore } from '@/onenetwork/stores/onenetwork.store.auth.ts'
import { useOneNetworkMenuStore } from '@/onenetwork/stores/onenetwork.store.menu.ts'
import { useRouter } from 'vue-router'
import CsAvatar from '@/onenetwork/components/csimage/CsAvatar.vue'
import { getFirstCharacterName } from '@/utils/helper.ts'
import { useOneNetworkNotificationStore } from '@/onenetwork/stores/onenetwork.store.notification.ts'
import { useToast } from 'primevue/usetoast'
import moment from 'moment'
import ChangePWPanel from '@/modules/account/components/ChangePWPanel.vue'
import Modal from '../components/modal/Modal.vue'
import AccountBODetail from '@/modules/account/views/AccountBODetail.vue'

const AuthStore = useAuthStore()
const MenuStore = useOneNetworkMenuStore()
const SiteNotificationStore = useOneNetworkNotificationStore()
const router = useRouter()
const toast = useToast()
const displayChangePWPanel = ref(false)
const showAccount = ref(false)
const notifications = computed(() => {
  return SiteNotificationStore.items.slice().reverse();
})

const totalUnreadCount = computed(() => {
  return SiteNotificationStore.totalUnread
})

const _version = computed(() => {
  return __APP_VERSION__
})

const props = defineProps({
  pageTitle: {
    type: String,
    default: 'Dashboard',
  },
  class: {
    type: String,
    default: '',
  },
  currentRoute: {
    type: String,
    default: '',
  },
  tabs: {
    type: Array,
    default: [],
  },
  tabsEvent: {
    type: Object,
    required: false,
  },
})

const currentRoute = computed(() => {
  return props.currentRoute.split('.')
})


const authUser = computed(() => {
  return AuthStore.user
})

const openMenu = computed(() => {
  return MenuStore.openMenu
})

const handleLogout = () => {
  AuthStore.logout()
}

const handleToggleMenu = () => {
  MenuStore.toggleOpenMenu()
}

const handleGotoGuide = () => {
  router.push({ name: 'support-guide' })
}


const handleDetailNotification = (e: Event, item: any) => {

  SiteNotificationStore.readNotification(item)
  router.push({ name: 'contracts-list', query: { detailId: 21 } })
}

const handleRemoveNotification = (e: Event, item: any) => {

  SiteNotificationStore.removeNotification(item)
}

const handleReadAll = () =>{

  SiteNotificationStore.readAllNotification()
}
const handleClearAll = () =>{

  SiteNotificationStore.removeAllNotifications()
}


onMounted(() => {
  document.title = props.pageTitle
  const menuItems = document.querySelectorAll('aside .menu-item')
  if(menuItems && menuItems.length > 0) {
    // console.log(73, menuItems)
    menuItems.forEach(item => {
      item.classList.remove('active')
      if(item.getAttribute('data-path') && item.getAttribute('data-path').includes(props.currentRoute)){
        item.classList.add('active')
      }
    })
  }
})

watch(currentRoute, (newValue) => {}, { immediate: true })

watch(()=>notifications.value, (newValue) => {
  const lastMess = notifications.value[notifications.value.length - 1]
  // toast.add({ severity: 'success', summary: lastMess?.notification?.title || "Cập nhật mới", detail: lastMess?.notification?.body, life: 5000});
}, { immediate: true })


</script>

<template>
  <div class="dash-header">
      <div class="dash-heading">
        <div class="header_title">
          <h1 class="flex items-center gap-2">
            <span class="menu-toggle cursor-pointer" @click="handleToggleMenu">
              <template v-if="openMenu"><Icon icon="line-md:menu-unfold-left" height="24" /></template>
              <template v-else><Icon icon="line-md:menu-fold-right" height="24" /></template>
            </span>
            {{ props.pageTitle }}
          </h1>
        </div>
        <div class="dash-heading__actions">

          <div class="support flex items-center gap-2 text-[#5d99b4] hover:text-[#ef4e00] cursor-pointer" @click="handleGotoGuide">
            <Icon icon="fluent:book-question-mark-24-filled" height="24px" /> Hướng dẫn nghiệp vụ
          </div>
          <div class="notification">
            <div class="trigger">
              <div class="icon">
                <Icon icon="mdi:bell"/>
              </div>
              <div class="pulse" v-if="totalUnreadCount > 0"></div>
              <div class="marker" v-if="totalUnreadCount > 0">{{totalUnreadCount}}</div>
            </div>
            <div class="notification-list">
              <div class="notification-heading">
                <h3>Thông báo</h3>
                <div class="action" v-if="notifications && notifications?.length > 0">
                  <span @click="handleReadAll">Đọc hết tất cả</span>
                  <span @click="handleClearAll">Xoá hết</span>
                </div>
              </div>
              <perfect-scrollbar>
                <ul v-if="notifications && notifications?.length > 0">
                  <li v-for="(item, idx) in notifications" :key="`noti_item_${idx}`" :class="`${item.read ? 'read' : ''}`"
                      @click="(e)=>handleDetailNotification(e, item)"
                  >
                    <small>{{moment.unix(item.data["google.c.a.ts"]).format('HH:mm:ss DD/MM/YYYY')}}</small>
                    <h4>{{item.notification.title}}</h4>
                    <p>{{item.notification.body}}</p>
                    <div class="remove-btn"
                         @click="(e)=>handleRemoveNotification(e, item)">
                      <Icon icon="mingcute:close-fill" />
                    </div>
                  </li>
                </ul>
                <div class="empty" v-else>
                  <div class="icon">
                    <Icon icon="solar:file-smile-line-duotone" />
                  </div>
                  Chưa có thông báo trong phiên.
                </div>

              </perfect-scrollbar>
            </div>
          </div>
          <div class="account">
            <div class="cs-dropdown">
              <div class="cs-dropdown__trigger">
                Xin chào!
                <div class="bo-avatar"><cs-avatar :src="authUser?.avatar?.url?.thumbnail" :label="getFirstCharacterName(authUser?.full_name)" /></div>
                <code>[#{{authUser?.code}}]</code> <strong>{{authUser?.full_name}}</strong>

                <ul class="cs-dropdown__trigger-dropdown-list">
                  <li class="cursor-pointer" @click="() => {
                    showAccount = true
                  }">Hồ sơ</li>
                  <li class="cursor-pointer" @click="() => {
                    displayChangePWPanel = true
                  }">Đổi mật khẩu</li>
                  <li class="divider"></li>
                  <li><a @click="handleLogout">Đăng xuất</a></li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="dash-tabs" v-if="props.tabs.length > 0">
        <div class="dash-tabs__item" v-for="(tab, index) in props.tabs" :key="`tabItem${index}`" :class="`${tab.active ? 'active' : ''} ${index === 0 ? 'root-tab' : ''}`" @click="props.tabsEvent?.select(tab)">
          <div class="dash-tabs__item-label">
            {{ tab.label }}
          </div>
          <div class="dash-tabs__close-btn" @click="props.tabsEvent?.close(tab)">
            <Icon icon="mdi:close" />
          </div>
        </div>
        <div v-if="props.tabs.length > 1" class="dash-tabs__closeAll" @click="props.tabsEvent?.closeAll()">Đóng tất cả</div>
      </div>
  </div>
      <PerfectScrollbar>
        <div :class="`dash-content ${props.class}`">
          <template>
            <Message severity="error" class="w-full mb-5" closable>
              <template #icon><Icon icon="fluent:warning-32-filled" height="32px" /></template>
              <div class="ml-2" v-html="`<strong>Thông báo: </strong> Hệ thống tiến hành cập nhật trong thời gian từ 14:00 đến 16:00 ngày 25/05/2025.`"/>
            </Message>
            <Message severity="warn" class="w-full mb-5" closable>
              <template #icon><Icon icon="fluent:warning-32-filled" height="32px" /></template>
              <div class="ml-2" v-html="`<strong>Thông báo: </strong> Hệ thống tiến hành cập nhật trong thời gian từ 14:00 đến 16:00 ngày 25/05/2025.`"/>
            </Message>
            <Message severity="success" class="w-full mb-5" closable>
              <template #icon><Icon icon="ooui:success" height="32px" /></template>
              <div class="ml-2" v-html="`<strong>Thông báo: </strong> Hệ thống tiến hành cập nhật trong thời gian từ 14:00 đến 16:00 ngày 25/05/2025.`"/>
            </Message>
            <Message severity="info" class="w-full mb-5" closable>
              <template #icon><Icon icon="fluent:chat-warning-16-filled" height="32px" /></template>
              <div class="ml-2" v-html="`<strong>Thông báo: </strong> Hệ thống tiến hành cập nhật trong thời gian từ 14:00 đến 16:00 ngày 25/05/2025.`"/>
            </Message>
          </template>
          <slot></slot>
        </div>
      </PerfectScrollbar>
      <div class="flex px-[32px]">&copy;2025. One Network Platform ({{_version}})</div>
      <Modal :show="displayChangePWPanel" size="sm" title="Đổi mật khẩu" @on:close="()=>{displayChangePWPanel = false}">
        <ChangePWPanel :account="authUser" @update:closeModal="()=>{displayChangePWPanel = false}" />
      </Modal>
      <Drawer v-model:visible="showAccount" header="Thông tin hồ sơ" position="right">
        <div class="drawer-80vw">
          <AccountBODetail :id="authUser?.id"/>
        </div>
      </Drawer>
</template>

<style scoped></style>
